import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, EventEmitter, HostBinding, Inject, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { EDITOR_INLINE_EDIT } from '../editor-config';
import { Options } from 'select2';
import { DashboardService } from '../../dashboard.service';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

export interface DialogData {
  _id: string;
  templateName: string;
  templateType: string;
  content: any;
  heading: string;
  update: boolean;
  subject: string;
  templatePlaceholder: string;
  difficultyLevel?: string;
}

export interface Tag {
  name: string;
}



@Component({
  selector: 'app-create-update-oeq',
  templateUrl: './create-update-oeq.component.html',
  styleUrls: ['./create-update-oeq.component.scss']
})

export class CreateUpdateOeqComponent implements OnInit {

  @Output() createUpdateOeq = new EventEmitter<string>();
  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  tags: Tag[] = [];
  createOeqFrom: UntypedFormGroup;
  difficultyLevels = [{ id: 'LOW', name: 'Easy' }, { id: 'MEDIUM', name: 'Medium' }, { id: 'HIGH', name: 'Hard' }];
  minutes: number[] = [];

  oeEditorConfig: AngularEditorConfig = EDITOR_INLINE_EDIT;

  topics = [];
  private topicsPageNo: number = 1;
  private totalTopics: number = 0;
  private topicsSearchTerm: string = '';
  topicsSettings = {
    singleSelection: false,
    idField: '_id',
    textField: 'topic',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    allowSearchFilter: true,
    allowRemoteDataSearch: true
  };


  subTopicsSettings = {
    singleSelection: false,
    idField: '_id',
    textField: 'name',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    allowSearchFilter: true,
    allowRemoteDataSearch: true
  };
  subTopics = [];
  private subTopicsPageNo: number = 1;
  private totalSubTopics: number = 0;
  private searchTerm: string = '';
  private limit: number = 50;
  sourceSubTopics = [];


  private subscriptions: Subscription[] = [];
  private filterSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private topicFilterSubject = new Subject<string>();

  constructor(public dialogRef: MatDialogRef<CreateUpdateOeqComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private fb: UntypedFormBuilder,
    private dashboardService: DashboardService) {
    this.createOeqFrom = this.fb.group({
      hourLimit: [0],
      minLimit: [5, [Validators.required]],
      questionType: ['OPEN_ENDED', [Validators.required]],
      questionText: ['', [Validators.required]],
      answer: [''],
      selectedTopics: [[], [Validators.required]],
      difficultyLevel: [this.data?.difficultyLevel || '', [Validators.required]],
      subTopics: [[]]
    });
  }

  /**
   * Utility method to deduplicate topics based on _id
   * @param topics Array of topic objects
   * @returns Deduplicated array of topics
   */
  private deduplicateTopics(topics: { _id: string, topic: string }[]): { _id: string, topic: string }[] {
    const seen = new Set<string>();
    return topics.filter(topic => {
      if (seen.has(topic._id)) {
        return false;
      }
      seen.add(topic._id);
      return true;
    });
  }

  ngOnInit(): void {
    this.getTopics('');
    this.minutes = [];
    for (let index = 1; index < 60; index++) {
      this.minutes.push(index);
    }
    this.filterSubject.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe((searchTerm) => {
      this.getSubTopics(searchTerm);
    });
    this.topicFilterSubject.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe((searchTerm) => {
      this.getTopics(searchTerm);
    });
  }

  hasSelectedTopics(): boolean {
    const topics = this.createOeqFrom.value.selectedTopics || [];
    return topics.length > 0;
  }

  onTopicItemSelect(item): void {
    this.getSubTopics('');
  }

  onTopicDeselect(item): void {
    // If all topics are deselected, clear sub-topics
    if (!this.hasSelectedTopics()) {
      this.createOeqFrom.patchValue({ subTopics: [] });
      this.subTopics = [];
      this.totalSubTopics = 0;
    } else {
      // Refresh sub-topics for remaining topics
      this.getSubTopics('');
    }
  }

  getNextBatchSubTopics() {
    this.subTopicsPageNo++;
    if (this.totalSubTopics > this.subTopics?.length) {
      this.getSubTopics(this.searchTerm, 'nextBatch');
    }
  }

  getSubTopics(serchTerm, nextBatch = ''): void {
    const topicIds = this.createOeqFrom.value.selectedTopics.map(item => item._id).join(',');

    // Don't fetch sub-topics if no topics are selected
    if (!topicIds || topicIds.trim() === '') {
      this.subTopics = [];
      this.totalSubTopics = 0;
      return;
    }

    if (nextBatch !== 'nextBatch') {
      this.subTopicsPageNo = 1;
    }
    this.searchTerm = serchTerm;
    const reqObj = {
      pageNo: this.subTopicsPageNo,
      limit: this.limit,
      topicIds: topicIds.trim(),
      searchWord: this.searchTerm
    };
    this.subscriptions.push(
      this.dashboardService.getSubTopics(reqObj).subscribe((response: any) => {
        let res = response?.docs?.map(t => {
          t.name = `${t.name}  (${t.topic.topic})`
          return t;
        });
        if (nextBatch === 'nextBatch') {
          this.subTopics = [...this.subTopics, ...res];
        } else {
          this.subTopics = [...res];
        }
        this.totalSubTopics = response.total;
      })
    );
  }

  onFilterChange(search: string) {
    this.filterSubject.next(search);
  }

  getNextBatchTopics() {
    this.topicsPageNo++;
    if (this.totalTopics > this.topics?.length) {
      this.getTopics(this.topicsSearchTerm, 'nextBatch');
    }
  }

  getTopics(searchTerm = '', nextBatch = ''): void {
    if (nextBatch !== 'nextBatch') {
      this.topicsPageNo = 1;
    }
    this.topicsSearchTerm = searchTerm;
    const params = { pageNo: this.topicsPageNo, limit: 20, searchWord: this.topicsSearchTerm };
    this.dashboardService.getEnterpriseTopics(params).subscribe((response: any) => {
      // Deduplicate API response topics
      const deduplicatedResponseTopics = this.deduplicateTopics(response.topics || []);

      if (nextBatch === 'nextBatch') {
        // Merge with existing topics and deduplicate
        const combinedTopics = [...this.topics, ...deduplicatedResponseTopics];
        this.topics = this.deduplicateTopics(combinedTopics);
      } else {
        this.topics = deduplicatedResponseTopics;
      }
      this.totalTopics = response.total;
    });
  }

  onTopicFilterChange(search: string) {
    this.topicFilterSubject.next(search);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  addOrUpdateQuestion(): void {
    let topics = [];
    let subTopics = [];
    topics.push(...this.createOeqFrom.get('selectedTopics').value.map(topic => topic._id));
    subTopics.push(...this.createOeqFrom.value?.subTopics.map(i => i._id))
    const tempObj = {
      update: this.data?.update,
      _id: this.data?.content._id,
      topics: topics,
      subTopics: subTopics
    }
    delete this.createOeqFrom.value['selectedTopics'];
    const quesData = { ...this.createOeqFrom.value, ...tempObj };
    this.createUpdateOeq.emit(quesData);
  }

  addTag(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.tags.push({ name: value });
    }
    event.chipInput!.clear();
  }

  removeTag(tag: Tag): void {
    const index = this.tags.indexOf(tag);
    if (index >= 0) {
      this.tags.splice(index, 1);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.forEach(sb => sb.unsubscribe());
  }

}
